import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auditFetch } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const emptyPanel = {
  headline: {
    teleconsultations: 0,
    attendedPatients: 0,
    activeDoctors: 0,
    averageWaitMinutes: 0,
    satisfaction: 0,
    traceabilityEvents: 0,
  },
  careFlow: [],
  security: { auditedCoverage: 0, auditEvents: 0, modules: [], rbac: 'Activo', dataProtection: 'Consentimiento y bitacora' },
  productivity: { averageConsultationMinutes: 0, completedRate: 0, cancelNoShowRate: 0, doctors: [], specialties: [] },
  satisfaction: { average: 0, responses: 0, promoters: 0, observation: 'Sin encuestas registradas' },
  traceability: { recent: [], clinicalDocuments: 0, pendingReferences: 0, pendingFollowUps: 0 },
  patientBase: { total: 0, active: 0 },
};

const ExecutivePresentationPanel = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [panel, setPanel] = useState(emptyPanel);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  const loadPanel = useCallback(() => {
    setIsLoading(true);
    setStatusMessage('');
    const endpoint = user.demoMode ? '/api/demo/presentation' : '/api/admin/executive-panel';
    auditFetch(`${backendUrl}${endpoint}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudo cargar el panel ejecutivo'))))
      .then((data) => setPanel(user.demoMode ? (data.executivePanel || emptyPanel) : (data.panel || emptyPanel)))
      .catch((error) => {
        console.error('Error loading executive panel:', error);
        setStatusMessage('No se pudo cargar el panel ejecutivo.');
      })
      .finally(() => setIsLoading(false));
  }, [auditActor, backendUrl, user.demoMode]);

  useEffect(() => {
    loadPanel();
  }, [loadPanel]);

  const headline = panel.headline || emptyPanel.headline;
  const productivity = panel.productivity || emptyPanel.productivity;
  const satisfaction = panel.satisfaction || emptyPanel.satisfaction;
  const traceability = panel.traceability || emptyPanel.traceability;
  const security = panel.security || emptyPanel.security;
  const maxSpecialty = Math.max(1, ...(productivity.specialties || []).map((item) => item.count));

  return (
    <section className="executive-panel">
      <div className="executive-hero">
        <div>
          <span className="executive-eyebrow">Panel para presentacion ejecutiva</span>
          <h2>Telemedicina en operacion</h2>
          <p>
            Metricas clave para junta: atencion, seguridad, productividad, satisfaccion y trazabilidad.
            {user.demoMode ? ' Datos ficticios activos.' : ''}
          </p>
        </div>
        <div className="executive-hero-actions">
          <span>{panel.range?.from || '30 dias'} - {panel.range?.to || 'hoy'}</span>
          <button className="btn btn-secondary" onClick={loadPanel} disabled={isLoading}>
            {isLoading ? 'Actualizando...' : 'Actualizar panel'}
          </button>
        </div>
      </div>

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      <div className="executive-kpi-grid">
        <div className="executive-kpi-card impact">
          <span>Teleconsultas</span>
          <strong>{headline.teleconsultations}</strong>
          <small>{headline.attendedPatients} pacientes atendidos</small>
        </div>
        <div className="executive-kpi-card">
          <span>Medicos activos</span>
          <strong>{headline.activeDoctors}</strong>
          <small>{productivity.averageConsultationMinutes} min por consulta</small>
        </div>
        <div className="executive-kpi-card">
          <span>Espera promedio</span>
          <strong>{headline.averageWaitMinutes} min</strong>
          <small>{productivity.completedRate}% cierre de atencion</small>
        </div>
        <div className="executive-kpi-card success">
          <span>Satisfaccion</span>
          <strong>{headline.satisfaction || '--'}</strong>
          <small>{satisfaction.promoters}% respuestas favorables</small>
        </div>
        <div className="executive-kpi-card">
          <span>Trazabilidad</span>
          <strong>{headline.traceabilityEvents}</strong>
          <small>{security.auditedCoverage}% cobertura operativa</small>
        </div>
      </div>

      <div className="executive-grid">
        <article className="executive-panel-card executive-wide">
          <div className="executive-card-header">
            <h3>Flujo de atencion</h3>
            <span>{traceability.clinicalDocuments} documentos clinicos</span>
          </div>
          <div className="executive-flow">
            {(panel.careFlow || []).map((step) => (
              <div key={step.name} className="executive-flow-step">
                <div>
                  <strong>{step.value}</strong>
                  <span>{step.name}</span>
                </div>
                <small>{step.rate}%</small>
              </div>
            ))}
          </div>
        </article>

        <article className="executive-panel-card">
          <div className="executive-card-header">
            <h3>Seguridad</h3>
            <span>{security.rbac}</span>
          </div>
          <div className="executive-score">
            <strong>{security.auditedCoverage}%</strong>
            <span>eventos con respaldo de bitacora</span>
          </div>
          <p className="executive-note">{security.dataProtection}</p>
          <div className="executive-module-list">
            {(security.modules || []).map((module) => (
              <div key={module.name}>
                <span>{module.name}</span>
                <strong>{module.count}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="executive-panel-card">
          <div className="executive-card-header">
            <h3>Productividad medica</h3>
            <span>{productivity.cancelNoShowRate}% cancel/no show</span>
          </div>
          <div className="executive-doctor-list">
            {(productivity.doctors || []).length === 0 ? (
              <p className="audit-empty">Sin consultas cerradas en el periodo.</p>
            ) : productivity.doctors.map((doctor) => (
              <div key={doctor.doctorId} className="executive-doctor-row">
                <strong>{doctor.doctorName}</strong>
                <span>{doctor.specialty}</span>
                <em>{doctor.consultations} consultas · {doctor.averageMinutes} min</em>
              </div>
            ))}
          </div>
        </article>

        <article className="executive-panel-card">
          <div className="executive-card-header">
            <h3>Satisfaccion del paciente</h3>
            <span>{satisfaction.responses} respuestas</span>
          </div>
          <div className="executive-score patient">
            <strong>{satisfaction.average || '--'}</strong>
            <span>{satisfaction.observation}</span>
          </div>
          <div className="executive-meter">
            <span style={{ width: `${Math.max(4, satisfaction.promoters || 0)}%` }} />
          </div>
          <small className="executive-footnote">{satisfaction.promoters}% con valoracion favorable</small>
        </article>

        <article className="executive-panel-card">
          <div className="executive-card-header">
            <h3>Especialidades con mayor demanda</h3>
            <span>{panel.patientBase?.active || 0} pacientes activos</span>
          </div>
          {(productivity.specialties || []).length === 0 ? (
            <p className="audit-empty">Sin demanda registrada.</p>
          ) : productivity.specialties.map((specialty) => (
            <div key={specialty.name} className="executive-bar-row">
              <div>
                <strong>{specialty.name}</strong>
                <span>{specialty.count}</span>
              </div>
              <div className="executive-bar-track">
                <span style={{ width: `${Math.max(8, (specialty.count / maxSpecialty) * 100)}%` }} />
              </div>
            </div>
          ))}
        </article>

        <article className="executive-panel-card executive-wide">
          <div className="executive-card-header">
            <h3>Trazabilidad reciente</h3>
            <span>{traceability.pendingReferences} referencias · {traceability.pendingFollowUps} seguimientos</span>
          </div>
          <div className="executive-trace-list">
            {(traceability.recent || []).length === 0 ? (
              <p className="audit-empty">Sin eventos recientes.</p>
            ) : traceability.recent.map((entry) => (
              <div key={entry.id} className="executive-trace-row">
                <strong>{entry.action} · {entry.module}</strong>
                <span>{entry.detail}</span>
                <em>{entry.actor} · {entry.date} {entry.time}</em>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
};

export default ExecutivePresentationPanel;
